import { actions, mutations } from "@/store";
import { invoke, start, stop } from "@/utils/identificationSignal";
import { get, put } from "@/utils/api";
import { adaptFraudChecks, adaptIdentification, adaptIdentificationSignal, adaptRegistries } from "@/store/work/adapters";
import { trackException } from "@/appInsights";
import { AppNotification } from "@/constants/appNotification";
import { CLIENT_CLOSE_REASONS, CLOSE_REASON } from "@/constants";

export default {
  workReload() {
    location.reload();
  },
  toggleOnline({ commit }, isOnline) {
    commit(mutations.toggleOnline, isOnline);
  },
  async workStart({ commit, getters, dispatch }) {
    try {
      commit(mutations.workStarting);
      await start({
        accessTokenFactory: () => getters.token,
        onClose: ({ reason, error }) => {
          commit(mutations.workStopped, error?.message);

          /* Work was stopped from in here — the Stop button, or the idle timer
             doing it for them, which raises its own notice. Either way the panel
             already reflects it, so there is nothing to announce. */
          if (CLIENT_CLOSE_REASONS.includes(reason)) return;

          if (reason === CLOSE_REASON.SERVER_REQUESTED) {
            /* The hub ending a session is a deliberate act, not a failure, so
               it reads as a notice rather than an error. */
            dispatch(actions.notificationAdd, {
              id: AppNotification.WorkClosed,
              title: "Work session ended",
              message: 'The service ended your work session. Select "Start working" to continue.',
              duration: 0,
              actions: [{ label: "Dismiss" }]
            });

            return;
          }

          dispatch(actions.notificationAdd, {
            id: AppNotification.WorkClosed,
            title: "Work stopped with error",
            message: error?.message ?? 'The connection could not be restored. Select "Start working" to try again.',
            duration: 0,
            actions: [{ label: "Dismiss" }]
          });
        },
        onIdentificationRefreshed: data => {
          const adaptedData = adaptIdentificationSignal(data);

          commit(mutations.initIdentification, adaptedData);
          dispatch(actions.getIdentification, adaptedData);
          commit(mutations.workSetIdentification, adaptedData);
        },
        onReconnecting: () => {
          commit(mutations.workReconnecting);
        },
        onReconnected: () => {
          commit(mutations.workReconnected);
        }
      });
      commit(mutations.workStarted);
    } catch (error) {
      trackException({
        exception: error,
        properties: { source: "store/work/workStart" }
      });
      commit(mutations.workStartError, error.message);
      dispatch(actions.notificationAdd, {
        id: AppNotification.WorkStartError,
        title: "Failed to start the work",
        message: `Reload the page and try again. ${error.message}`,
        duration: 0,
        actions: [{ label: "Dismiss" }, { label: "Reload", action: actions.workReload }]
      });
    }
  },
  async workStop({ dispatch, commit }, reason) {
    try {
      commit(mutations.workStopping);
      await stop(reason);
    } catch (error) {
      trackException({
        exception: error,
        properties: { source: "store/work/workStop" }
      });
      commit(mutations.workStopError, error.message);
      dispatch(actions.notificationAdd, {
        title: "Failed to stop the work",
        message: `Reload the page and try again. ${error.message}`,
        duration: 0,
        actions: [{ label: "Dismiss" }, { label: "Reload", action: actions.workReload }]
      });
    }
  },
  async workWaitForWork({ dispatch }) {
    try {
      await invoke("WaitForWork");
    } catch (error) {
      trackException({
        exception: error,
        properties: { source: "store/work/workWaitForWork" }
      });
      dispatch(actions.notificationAdd, {
        id: AppNotification.WaitForWorkError,
        title: "Could not ask for the next identification",
        message: `The next identification was not requested: ${error.message}`,
        duration: 0,
        actions: [{ label: "Dismiss" }, { label: "Retry", action: actions.workWaitForWork }]
      });
    }
  },
  async getIdentification({ dispatch, commit }, { identificationId }) {
    commit(mutations.getIdentificationInit, { identificationId });
    const url = `${process.env.VUE_APP_ARBITRIUM_API}/v1/identifications/${identificationId}`;

    try {
      const data = await get(url);
      const urlProcedures = `${process.env.VUE_APP_ARBITRIUM_API}/v1/identification-procedures?applicationId=${data.registration.applicationId}`;
      const dataProcedures = await get(urlProcedures);

      commit(mutations.getIdentificationSuccess, {
        identificationId,
        data: adaptIdentification({ ...data, procedures: dataProcedures })
      });
    } catch (error) {
      dispatch(actions.workWaitForWork);
      trackException({
        exception: error,
        properties: { source: "store/work/getIdentification" }
      });
      commit(mutations.getIdentificationError, {
        identificationId,
        error: error.message
      });
      dispatch(actions.notificationAdd, {
        id: AppNotification.GetIdentificationError,
        title: "Getting identification failed",
        message: `Id: "${identificationId}" has failed with error: "${error.message}"`,
        duration: 0,
        actions: [{ label: "Dismiss" }]
      });
    }
  },
  async identificationRegistryReprocess({ dispatch, commit }, payload) {
    const { identificationId } = payload;
    commit(mutations.identificationRegistryReprocessInit, {
      identificationId
    });
    const url = `${process.env.VUE_APP_ARBITRIUM_API}/v1/identifications/${identificationId}/reprocessregistries`;
    try {
      const { isSuccess, value } = await put(url);

      if (!isSuccess) {
        throw new Error("Reprocess failed");
      }

      commit(mutations.identificationRegistryReprocessSuccess, {
        identificationId,
        data: {
          registries: adaptRegistries(value.registries),
          fraudChecks: adaptFraudChecks(value.fraudChecks)
        }
      });
    } catch (error) {
      trackException({
        exception: error,
        properties: { source: "store/work/identificationRegistryReprocess" }
      });
      commit(mutations.identificationRegistryReprocessError, {
        identificationId,
        error: error.message
      });
      dispatch(actions.notificationAdd, {
        id: AppNotification.ReprocessidentificationError,
        title: "Identification reprocess failed",
        message: `Error: ${error.message}`,
        duration: 0,
        actions: [
          { label: "Dismiss" },
          {
            label: "Retry",
            action: actions.identificationRegistryReprocess,
            payload
          }
        ]
      });
    }
  },
  async putIdentificationDocument({ dispatch, commit }, { identificationId, document }) {
    commit(mutations.putIdentificationDocumentInit, { identificationId });
    const url = `${process.env.VUE_APP_ARBITRIUM_API}/v1/identifications/${identificationId}/document`;
    try {
      await put(url, document);
      commit(mutations.putIdentificationDocumentSuccess, {
        identificationId,
        document
      });
      dispatch(actions.getIdentification, { identificationId });
    } catch (error) {
      trackException({
        exception: error,
        properties: { source: "store/work/putIdentificationDocument" }
      });
      commit(mutations.putIdentificationDocumentError, {
        identificationId,
        error: error.message
      });
      dispatch(actions.notificationAdd, {
        id: AppNotification.DocumentSubmitError,
        title: "Failed to submit identification document",
        message: error.message
      });
    }
  },
  async invokeIdentificationReroute({ commit, dispatch }) {
    try {
      const { isSuccessfullyRerouted, message } = await invoke("Reroute");
      if (isSuccessfullyRerouted) {
        dispatch(actions.notificationAdd, {
          id: AppNotification.IdentificationRerouteSuccess,
          title: "Re-route completed successfully",
          actions: [{ label: "Dismiss" }]
        });
        commit(mutations.invokeIdentificationRerouteSuccess);
      } else {
        dispatch(actions.notificationAdd, {
          id: AppNotification.IdentificationRerouteError,
          title: "Re-route error",
          message,
          duration: 0,
          actions: [{ label: "Dismiss" }, { label: "Retry", action: actions.invokeIdentificationReroute }]
        });
      }
    } catch (error) {
      trackException({
        exception: error,
        properties: { source: "store/work/invokeIdentificationReroute" }
      });
      dispatch(actions.notificationAdd, {
        id: AppNotification.IdentificationRerouteError,
        title: "Re-route error",
        message: error.message,
        duration: 0,
        actions: [{ label: "Dismiss" }, { label: "Retry", action: actions.invokeIdentificationReroute }]
      });
    }
  },
  async putIdentificationApprove({ dispatch, commit }, payload) {
    const { identificationId } = payload;
    commit(mutations.putIdentificationApproveInit, { identificationId });
    const url = `${process.env.VUE_APP_ARBITRIUM_API}/v1/identifications/${identificationId}/approve`;
    try {
      const data = await put(url);
      commit(mutations.putIdentificationApproveSuccess, {
        identificationId,
        data
      });
      dispatch(actions.workWaitForWork);

      return true;
    } catch (error) {
      trackException({
        exception: error,
        properties: { source: "store/work/putIdentificationApprove" }
      });
      commit(mutations.putIdentificationApproveError, {
        identificationId,
        error: error.message
      });
      dispatch(actions.notificationAdd, {
        id: AppNotification.IdentificationApprove,
        title: "Identification approval has failed",
        message: `Approving "${identificationId}" has failed with error "${error.message}"`,
        duration: 0,
        actions: [{ label: "Dismiss" }, { label: "Retry", action: actions.putIdentificationApprove, payload }]
      });

      return false;
    }
  },
  async putIdentificationReject({ dispatch, commit }, payload) {
    const { identificationId, reason } = payload;
    commit(mutations.putIdentificationRejectInit, { identificationId });
    const url = `${process.env.VUE_APP_ARBITRIUM_API}/v1/identifications/${identificationId}/reject`;
    try {
      const data = await put(url, { reason });
      commit(mutations.putIdentificationRejectSuccess, {
        identificationId,
        data
      });
      dispatch(actions.workWaitForWork);

      return true;
    } catch (error) {
      trackException({
        exception: error,
        properties: { source: "store/work/putIdentificationReject" }
      });
      commit(mutations.putIdentificationRejectError, {
        identificationId,
        error: error.message
      });
      dispatch(actions.notificationAdd, {
        id: AppNotification.IdentificationRejectError,
        title: "Identification rejection has failed",
        message: `Rejecting "${identificationId}" with reason "${reason}" has failed with error "${error.message}"`,
        duration: 0,
        actions: [{ label: "Dismiss" }, { label: "Retry", action: actions.putIdentificationReject, payload }]
      });

      return false;
    }
  },
  async putProofOfAddress({ dispatch, commit }, { identificationId, poa }) {
    commit(mutations.putIdentificationPOAInit, { identificationId });
    const url = `${process.env.VUE_APP_ARBITRIUM_API}/v1/identifications/${identificationId}/proof-of-address`;
    try {
      await put(url, poa);
      commit(mutations.putIdentificationPOASuccess, {
        identificationId,
        poa
      });
      dispatch(actions.getIdentification, { identificationId });
    } catch (error) {
      trackException({
        exception: error,
        properties: { source: "store/work/putProofOfAddress" }
      });
      commit(mutations.putIdentificationPOAError, {
        identificationId,
        error: error.message
      });
      dispatch(actions.notificationAdd, {
        id: AppNotification.DocumentSubmitError,
        title: "Failed to submit identification proof of address",
        message: error.message
      });
    }
  }
};
